<template>
  <div ref="scrollbarRef" :class="namespace">
    <div
      ref="wrapRef"
      :class="[
        wrapClass,
        'scrollbar-wrap',
        native ? '' : 'scrollbar-wrap-hidden',
      ]"
      :style="style"
      @scroll="handleScroll"
    >
      <component
        :is="tag"
        :id="id"
        ref="resizeRef"
        :class="['scrollbar-view', viewClass]"
        :style="viewStyle"
        :role="role"
        :aria-label="ariaLabel"
        :aria-orientation="ariaOrientation"
      >
        <slot></slot>
      </component>
    </div>
    <template v-if="!native">
      <bar ref="barRef" :always="always" :min-size="minSize" />
    </template>
  </div>
</template>
<script lang="ts">
import {
  computed,
  defineComponent,
  nextTick,
  onActivated,
  onMounted,
  provide,
  ref,
  watch,
  reactive,
  onUpdated,
} from 'vue'
import { useResizeObserver, useEventListener } from '@vueuse/core'
import { debugWarn, addUnit, isNumber, isObject } from '@hongluan-ui/utils'
import { useNamespace } from '@hongluan-ui/hooks'
import { scrollbarContextKey } from '@hongluan-ui/tokens'
import Bar from './bar.vue'

import { scrollbarProps } from './scrollbar'
import type { StyleValue, CSSProperties } from 'vue'

export default defineComponent({
  name: 'Scrollbar',
  components: {
    Bar,
  },
  props: scrollbarProps,
  emits: ['scroll', 'at-end'],

  setup(props, { emit }) {
    let stopResizeObserver: (() => void) | undefined = undefined
    let stopResizeListener: (() => void) | undefined = undefined
    let wrapScrollTop = 0
    let wrapScrollLeft = 0
    let oldScrollTop = Number.MAX_SAFE_INTEGER

    const scrollbarRef = ref<HTMLDivElement>()
    const wrapRef = ref<HTMLDivElement>()
    const resizeRef = ref<HTMLElement>()
    const barRef = ref()

    const SCOPE = 'Scrollbar'

    const style = computed<StyleValue>(() => {
      const style: CSSProperties = {}
      if (props.height) style.height = addUnit(props.height)
      if (props.maxHeight) style.maxHeight = addUnit(props.maxHeight)
      return [props.wrapStyle, style]
    })

    const handleScroll = () => {
      if (wrapRef.value) {
        barRef.value?.handleScroll(wrapRef.value)
        wrapScrollTop = wrapRef.value.scrollTop
        wrapScrollLeft = wrapRef.value.scrollLeft

        emit('scroll', {
          scrollTop: wrapRef.value.scrollTop,
          scrollLeft: wrapRef.value.scrollLeft,
        })

        if (wrapRef.value.scrollTop > oldScrollTop && wrapRef.value.scrollTop + wrapRef.value.offsetHeight + props.distance >= wrapRef.value.scrollHeight) {
          emit('at-end', { vertical: true })
        }
        oldScrollTop = wrapRef.value.scrollTop
      }
    }

    function scrollTo(xCord: number, yCord?: number): void
    function scrollTo(options: ScrollToOptions): void
    function scrollTo(arg1: unknown, arg2?: number) {
      if (isObject(arg1)) {
        wrapRef.value.scrollTo(arg1)
      } else if (isNumber(arg1) && isNumber(arg2)) {
        wrapRef.value.scrollTo(arg1, arg2)
      }
    }

    const setScrollTop = (value: number) => {
      if (!isNumber(value)) {
        debugWarn(SCOPE, 'value must be a number')
        return
      }
      wrapRef.value.scrollTop = value
    }

    const setScrollLeft = (value: number) => {
      if (!isNumber(value)) {
        debugWarn(SCOPE, 'value must be a number')
        return
      }
      wrapRef.value.scrollLeft = value
    }

    const update = () => {
      barRef.value?.update()
    }

    watch(
      () => props.noresize,
      noresize => {
        if (noresize) {
          stopResizeObserver?.()
          stopResizeListener?.()
        } else {
          ({ stop: stopResizeObserver } = useResizeObserver(resizeRef, update))
          stopResizeListener = useEventListener('resize', update)
        }
      },
      { immediate: true },
    )

    provide(
      scrollbarContextKey,
      reactive({
        scrollbarElement: scrollbarRef,
        wrapElement: wrapRef,
      }),
    )
    onActivated(() => {
      wrapRef.value!.scrollTop = wrapScrollTop
      wrapRef.value!.scrollLeft = wrapScrollLeft
    })

    onMounted(() => {
      if (!props.native) {
        nextTick(() => {
          update()
        })
      }
    })
    onUpdated(() => update())

    const { namespace } = useNamespace('scrollbar')
    return {
      namespace,
      scrollbarRef,
      wrapRef,
      resizeRef,
      barRef,

      style,
      update,
      handleScroll,
      setScrollTop,
      setScrollLeft,
      scrollTo,
    }
  },
})
</script>
